import React, { useState } from 'react';
import { useParams, Navigate, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Check, ShoppingCart, ArrowLeft } from 'lucide-react';
import { findProduct, products } from '../data/products';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import MerchHeader from '../components/MerchHeader';
import Footer from '../components/Footer';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { addItem } = useCart();
  const p = findProduct(slug);
  const [size, setSize] = useState(p?.sizes?.[0] || '');
  const [qty, setQty] = useState(1);

  if (!p) return <Navigate to="/merch" replace />;

  const related = products.filter((x) => x.slug !== p.slug).slice(0, 3);

  const handleAdd = () => {
    if (!size) {
      toast({ title: 'Pick a size first' });
      return;
    }
    addItem({
      slug: p.slug,
      name: p.name,
      price: p.price,
      image: p.image,
      size,
      quantity: qty,
    });
    toast({
      title: 'Added to cart',
      description: `${p.name} · Size ${size} · Qty ${qty}`,
    });
  };

  const handleBuyNow = () => {
    if (!size) {
      toast({ title: 'Pick a size first' });
      return;
    }
    addItem({ slug: p.slug, name: p.name, price: p.price, image: p.image, size, quantity: qty });
    navigate('/merch/cart');
  };

  return (
    <div className="min-h-screen bg-[#f8f4ee]">
      <MerchHeader />

      <main className="max-w-[1200px] mx-auto px-4 md:px-6 pt-10 md:pt-14 pb-20">
        <button
          onClick={() => navigate('/merch')}
          className="flex items-center gap-1 text-sm text-[#5a5148] hover:text-[#0a1030] mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back to shop
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-14">
          {/* Image */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-black/5">
            <div className="aspect-square bg-[#f4f2ef]">
              <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Info */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-[#8a7a4a] font-semibold">
              {p.category}
            </p>
            <h1
              className="mt-2 text-[36px] md:text-[46px] text-[#0a1030] leading-tight"
              style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
            >
              {p.name}
            </h1>
            <div className="mt-3 text-[28px] font-semibold text-[#0a1030]">${p.price}</div>

            <p className="mt-6 text-[15px] md:text-[16px] text-[#3a3a3a] leading-relaxed">
              {p.description}
            </p>

            <ul className="mt-5 space-y-2">
              {p.highlights.map((h) => (
                <li key={h} className="flex items-start gap-2 text-[14px] text-[#3a3a3a]">
                  <Check className="h-4 w-4 mt-0.5 text-[#0a1030]" strokeWidth={3} />
                  {h}
                </li>
              ))}
            </ul>

            {/* Size */}
            <div className="mt-8">
              <div className="text-[13px] font-semibold text-[#1a1a1a] mb-3">
                {p.category === 'Hat' ? 'Size' : 'Select a size'}
              </div>
              <div className="flex flex-wrap gap-2">
                {p.sizes.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSize(s)}
                    className={`min-w-[52px] h-11 px-4 rounded-md border text-[14px] font-medium transition-colors ${
                      size === s
                        ? 'bg-[#0a1030] border-[#0a1030] text-white'
                        : 'bg-white border-black/15 text-[#3a3a3a] hover:border-[#0a1030]'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mt-6">
              <div className="text-[13px] font-semibold text-[#1a1a1a] mb-3">Quantity</div>
              <div className="inline-flex items-center border border-black/15 rounded-md bg-white">
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-10 h-11 text-lg text-[#3a3a3a] hover:bg-[#f4f2ef] rounded-l-md"
                >
                  −
                </button>
                <div className="w-12 text-center font-semibold">{qty}</div>
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.min(10, q + 1))}
                  className="w-10 h-11 text-lg text-[#3a3a3a] hover:bg-[#f4f2ef] rounded-r-md"
                >
                  +
                </button>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleAdd}
                variant="outline"
                className="rounded-full border-[#0a1030] text-[#0a1030] hover:bg-[#0a1030]/5 h-12 px-8"
              >
                <ShoppingCart className="h-4 w-4 mr-2" /> Add to cart
              </Button>
              <Button
                onClick={handleBuyNow}
                className="rounded-full bg-[#0a1030] hover:bg-[#111a4a] text-white h-12 px-8"
              >
                Buy now
              </Button>
            </div>

            <div className="mt-6 text-xs text-[#7a7a7a]">
              Pickup at 370 Tomkins Court, Gilroy CA. Usually ready by next Sunday.
            </div>
          </div>
        </div>

        {/* Related */}
        <div className="mt-24">
          <h3
            className="text-[28px] md:text-[34px] text-[#0a1030] mb-6"
            style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
          >
            More from the shop
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/merch/product/${r.slug}`}
                className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-md transition-all"
              >
                <div className="aspect-square overflow-hidden bg-[#f4f2ef]">
                  <img src={r.image} alt={r.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <div
                    className="text-[16px] text-[#0a1030] leading-tight"
                    style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
                  >
                    {r.name}
                  </div>
                  <div className="mt-1 text-sm font-semibold text-[#0a1030]">${r.price}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
      <Toaster />
    </div>
  );
};

export default ProductDetail;
